import { useEffect, useState } from 'react';
import { useAiImage } from './use-open-ai.js';
import { isFunction } from '#lib/utils/function.js';
import toast from 'react-hot-toast';
import { getSrcFromFile, isHttpUrl } from '#components/images/helpers.js';
import { checkImageFormat } from '#helpers/helpers.js';
import { useExternalImage } from './api/image-service.js';

export function useGalleryPanel(props) {
    const {
        files,
        defaultState = 'selecting',
        handleChangePreview,
        selectedImages,
        fetchImagesFromLibrary,
        isOpen,
        onClose,
        clearAll
    } = props;

    // Stati per la gestione del file in modifica
    const [editingFiles, setEditingFiles] = useState(files.filter(file => file));
    const [state, setState] = useState(defaultState)
    const [isEdited, setIsEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [toUpload, setToUpload] = useState([]);

    useEffect(() => {
        setEditingFiles(files.filter(file => file));
        setState(defaultState);
    }, [isOpen]);

    // Stati per la gestione del pannello
    const [optionId, setOptionId] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [hasError, setHasError] = useState(false);

    const handleInitCoords = (newCoords, i) => {
        const updated = structuredClone(editingFiles);
        const file = updated.find((_, index) => index === i);
        file.coords = newCoords;
        setEditingFiles(updated)
        setIsEdited(true)
    }

    // Handler per la modifica del file
    const handleAiImage = useAiImage({
        onSuccess: (res) => {
            const file = new Image();
            file.src = res.url;
            if (optionId === "D") {
                setState("editing");
                setEditingFiles(prev => ([...prev, { src: file.src, coords: { x: 0, y: 0 }, type: file.type, lastModified: file.lastModified }]));
            }
            setIsEdited(true);
            setIsLoading(false);
        },
        onError: (e) => {
            toast.error(e.message);
            setIsLoading(false);
        }
    });

    // Handler per il caricamento di immagini esterne su s3 temp
    const handleFetchExternalImage = useExternalImage({
        onSuccess: (res) => {
            const file = new Image();
            file.src = res.url;
            if (optionId === "E" || optionId === "L") {
                setState("editing");
                setEditingFiles(prev => ([...prev, { src: file.src, coords: { x: 0, y: 0 }, type: file.type, lastModified: file.lastModified }]));

            }
            setIsEdited(true);
            setIsLoading(false);
        },
        onError: (e) => {
            toast.error(e.message);
            setIsLoading(false);
        }
    })

    const handleUpdateFiles = async (e) => {
        const { target: { files } } = e;
        // Se stiamo caricando immagini da locale si controlla che siano effettivamente immagini
        if (!files || !files.length) {
            setState("selecting");
            return;
        }

        const filteredFiles = Array.from(files).filter(file => checkImageFormat(file));
        if (filteredFiles.length === 0) return;

        setToUpload(prev => ([...prev, ...filteredFiles]));

        const images = await Promise.all(
            Array.from(filteredFiles).map(async (f) => {
                const { name, type, lastModified } = f;
                const src = await getSrcFromFile(f);
                return { src, name, type, lastModified, sourceType: 'U' };
            }).filter((img) => img)
        );

        setEditingFiles(prev => {
            const newImages = [...prev, ...images];
            if (newImages.length === 0) setState("selecting");
            if (newImages.length > 0) {
                setState("editing");
                setIsEdited(true);
            }
            return (newImages);
        });
    };

    const handleGenerateImage = () => {
        if (!inputValue) {
            setHasError(true);
            return;
        }
        if (optionId === 'L') {
            fetchImagesFromLibrary(inputValue, 1);
            setIsEdited(true);
            return;
        }
        if (optionId === 'D') {
            handleAiImage.mutate({ prompt: inputValue });
            setIsLoading(true);
        }
        if (optionId === 'E') {
            if (!isHttpUrl(inputValue)) {
                setHasError(true);
                return toast.error(_t.errors.invalidImageFormat());
            }
            handleFetchExternalImage.mutate({ url: inputValue });
            setIsLoading(true);

        };
    };

    // Handler per la gestione del pannello
    const handleChangePanel = (id) => {
        setOptionId(id);
        if (id === "U") return;
        setState("uploading");
        setHasError(false);
        setInputValue('');
    };

    const handleChangeText = (e) => {
        setInputValue(e.target.value);
        if (e.target?.value.length > 0) setHasError(false);
    };

    const handleSelectLibraryImage = () => {
        if (!selectedImages.length) {
            toast.error('Please select an image');
            return;
        }
        setIsLoading(true);
        setState("editing");
        selectedImages.forEach((image) => {
            handleFetchExternalImage.mutate({ url: image.src });
        });
    };

    const handleDeleteEditingFile = (index) => {
        const updated = structuredClone(editingFiles);
        const files = updated.filter((_, i) => index !== i);
        setEditingFiles(files)
    }

    const handlePinFile = (index) => {
        const updated = structuredClone(editingFiles);
        const [item] = updated.splice(index, 1);
        updated.unshift(item);
        setEditingFiles(updated);
    }

    const handleSubmit = () => {
        if (isFunction(handleChangePreview))
            handleChangePreview(toUpload, editingFiles);
        setToUpload([]);
        handleClose();
    };


    const handleClose = (discard) => {
        setOptionId('');
        clearAll();
        onClose({ discard });
    };

    const handleClear = () => {
        setState("selecting")
        clearAll();
        setOptionId('');
    };

    return {
        // Gestione file in modifica
        state,
        setState,
        editingFiles,
        isEdited,
        isLoading,
        setIsLoading,
        handleInitCoords,
        handleIsEdited: setIsEdited,
        handleUpdateFiles,
        handleGenerateImage,
        // Gestione pannello
        optionId,
        inputValue,
        hasError,
        handleOptionId: setOptionId,
        handleInputValue: setInputValue,
        handleHasError: setHasError,
        handleChangePanel,
        handleChangeText,
        handleSelectLibraryImage,
        handleDeleteEditingFile,
        handlePinFile,
        handleSubmit,
        handleClose,
        handleClear
    };
}
